/**
 * 「이미지로 검색」 — 이미지 한 장과 비슷한 광고 소재 · 소셜 게시물 · 판매몰 상품. 앱 BFF `POST /api/search-all/image-search`(multipart `image`)의 응답.
 *
 * ⚠️ 이미지는 검색에만 쓰고 저장하지 않는다. 게시물 쪽은 외부 유사도 서비스를 거친다.
 * ⚠️ 받는 형식은 jpg · png · webp · gif, 15MB 까지다.
 */

import type { SimilarPostItem } from './similar-post';

/** 비슷한 광고 한 건 — 광고 id 와 점수만. 본문은 화면이 `ads.detail` 로 채운다 */
export type ImageSearchAdItem = {
  adArchiveId: string;
  tier: string;
  distance: number | null;
  cosine: number | null;
};

/**
 * 비슷한 상품 한 건 — 상품 이미지 하나가 여러 판매몰 상품으로 이어지면 상품마다 한 건이다.
 * ⚠️ 숫자는 `number | null` 이다 — 0 과 「값 없음」이 다르다(가격 0 은 미제공).
 */
export type ImageSearchProductItem = {
  /** 판매몰 코드(`musinsa` …) */
  source: string;
  postId: string;
  /** 몰이 적은 브랜드 원문(`account`) */
  brand: string;
  title: string;
  /** 비슷하다고 본 상품 이미지 */
  imageUrl: string;
  rank: number | null;
  /** 판매가 — 보조 단위(`salePriceMinor`)가 있으면 그것을 통화 소수 자리로 환산한 값 */
  salePrice: number | null;
  /** 없으면 `'KRW'` */
  currency: string;
  /** 그 스냅샷 날짜 `yyyyMMdd` */
  date: string;
  /** 제품군 소분류 — 없으면 빈 문자열 */
  category: string;
  cosine: number | null;
};

export type ImageSearchResponse = {
  /** 비슷한 순 */
  ads: ImageSearchAdItem[];
  /** 비슷한 순 */
  posts: SimilarPostItem[];
  /** 게시물 쪽만 실패했을 때의 문구 — 광고는 그대로 온다 */
  postsError: string;
  /** 비슷한 순 · 같은 상품(판매몰 + 상품 id)은 한 번만 */
  products: ImageSearchProductItem[];
  /** 상품 쪽만 실패했을 때의 문구 — 광고·게시물은 그대로 온다 */
  productsError: string;
};

/** 백엔드가 받는 형식 — HEIC 불가 */
export const IMAGE_SEARCH_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'] as const;

/** 백엔드가 받는 원본 크기 상한(byte) */
export const IMAGE_SEARCH_MAX_BYTES = 15 * 1024 * 1024;

/**
 * 광고 소재를 「비슷하다」고 볼 **CLIP 코사인 하한** — 이미지로 찾는 자리 전부가 이 값을 쓴다.
 *
 * 🚨 **안 보내면 상류 기본값 0.87 이 걸린다.** 그 값은 「비슷한 소재」가 아니라 «거의 같은 소재»
 *   문턱이라, 올린 이미지로 물으면 질의의 92% 가 0건이 된다(상류 실측).
 * 🔑 0.82 는 **사람이 보고 정한 운영값**이다(상류 문서 · 질의 36개 판정):
 *   ```
 *     0.82 ~ 0.87   괜찮음
 *     0.76 ~ 0.82   괜찮음      ← 재현율을 늘려야 할 때 내리는 바닥
 *     0.70 ~ 0.76   안 됨       ⛔ 여기까지 내리지 마라
 *   ```
 * ⚠️ **색인 밖 이미지는 점수가 체계적으로 낮다** — 광고끼리는 1위 중앙 0.864 인데 올린 이미지는
 *   0.801 이다. 그래서 0.82 면 0건이 늘어난다(상류 실측 63%). 너무 자주 비면 0.80 → 0.76 순으로
 *   내린다. ⛔ 0.76 아래로는 내리지 마라 — 사람이 「안 됨」이라 한 구간이다.
 * ⚠️ `clip=1` 을 **함께** 보내야 뜻이 있다 — 상류의 clip 티어가 기본으로 꺼져 있어, 안 켜면
 *   이 값을 어떻게 주든 0건이다.
 */
export const AD_CLIP_FLOOR = 0.82;
